import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, ViewChild, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { DsBannerState } from '@levelaccess/design-system';

import {
  $digitalProperty,
  $digitalPropertyMonitoring,
  $digitalPropertyMonitoringPagesLimitDefault,
  MonitoringDiscoveryMethod,
} from '../../../../../../../shared/constants/digital-properties';
import { $scan } from '../../../../../../../shared/constants/scan';
import { scanCreateRequest } from '../../../../../../../shared/constants/scan-create-request';
import { SCAN_URLS_SIZE_LIMIT_ERROR_KEY } from '../../../../../constants/scan.constants';
import { CustomValidators } from '../../../../../services/helpers/form-custom-validators';
import { TranslateService } from '../../../../../translate/translate.service';
import {
  IDigitalPropertyDetails,
  IDigitalPropertyMonitoringDetails,
  IDPMonitoringCrawlerDefinition,
  IDPMonitoringSitemapDefinition,
} from '../../../../../../../shared/interfaces/digital-property.interface';
import { ExcludePagesOperation, IScanHeader } from '../../../../../../../shared/interfaces/scan.interface';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { AutomatedScanAdvancedService } from '../../../../../pages/public/automated-scan/advanced/automated-scan-advanced.service';
import { FeatureFlagCollection } from '../../../../../../../shared/interfaces/feature-flag.interface';
import { scanAuthenticationType } from '../../../../../../../shared/constants/scanning';
import { ScanAuthenticationDetailsComponent } from '../../../../scan-authentication-details/scan-authentication-details.component';

const DEFAULT_MIN_VALUE = 1;

@Component({
  selector: 'app-monitoring-scope-step',
  templateUrl: './monitoring-scope-step.component.html',
  standalone: false,
})
export class MonitoringScopeStepComponent implements AfterViewInit, OnDestroy, OnInit {
  private availableDiscoveryMethods: MonitoringDiscoveryMethod[];
  public MonitoringDiscoveryMethod: typeof MonitoringDiscoveryMethod;
  private sitemapDomainExample: string;
  private subscriptions: Subscription;
  private isInitialized: boolean = false;

  @Input()
  public formValidationRequest$: Subject<void>;
  @Input()
  public form: UntypedFormGroup;
  @Input()
  public stepNumber: number;
  @Input()
  public set monitoring(value: IDigitalPropertyMonitoringDetails) {
    this._monitoring = value;
    this.excludeOperations = value?.[$digitalPropertyMonitoring.crawler]?.[$scan.excludeUrlOperations] ?? [];
    this.excludeUrls = value?.[$digitalPropertyMonitoring.crawler]?.[$scan.excludeUrls] ?? [];
  }
  public get monitoring(): IDigitalPropertyMonitoringDetails {
    return this._monitoring;
  }
  private _monitoring: IDigitalPropertyMonitoringDetails;

  @Input()
  public set digitalProperty(value: IDigitalPropertyDetails) {
    this._digitalProperty = value;
    // Immediately compute the cache to prevent ExpressionChangedAfterItHasBeenCheckedError
    this.cachedAuthorizedDomainsRegExp = this.domains.map(SharedCommonUtility.urlPatternToRegexp);
    // Update available methods after initialization to avoid timing issues
    if (this.isInitialized) {
      this.updateAvailableDiscoveryMethods();
    }
  }
  public get digitalProperty(): IDigitalPropertyDetails {
    return this._digitalProperty;
  }
  private _digitalProperty: IDigitalPropertyDetails;

  @ViewChild(ScanAuthenticationDetailsComponent, { static: true })
  private scanAuthenticationDetails: ScanAuthenticationDetailsComponent;

  public scanCreateRequest: typeof scanCreateRequest;
  public $digitalPropertyMonitoring: typeof $digitalPropertyMonitoring;
  protected readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;

  public canUsePreScanFlows$: Observable<boolean>;
  public discoveryMethodOptions: { key: string; value: string }[] = [];
  private cachedAuthorizedDomainsRegExp: RegExp[];
  public excludeOperations: ExcludePagesOperation[] = [];
  public excludeUrls: string[] = [];

  public get domains(): string[] {
    return this.digitalProperty?.[$digitalProperty.authorizedDomains] ?? [];
  }

  public get authorizedDomainsRegExp(): RegExp[] {
    return this.cachedAuthorizedDomainsRegExp ?? [];
  }

  public get isDpUrlPresent(): boolean {
    return SharedCommonUtility.notNullishOrEmpty(this.digitalProperty[$digitalProperty.url]);
  }

  public get sitemapExampleDomain(): string {
    return this.sitemapDomainExample ?? 'https://example.com';
  }

  public get maxNumberOfPages(): number {
    return this.digitalProperty?.[$digitalProperty.pagesLimit] ?? $digitalPropertyMonitoringPagesLimitDefault;
  }

  protected readonly DsBannerState: typeof DsBannerState = DsBannerState;

  constructor(
    private translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
    private advancedService: AutomatedScanAdvancedService,
  ) {
    this.MonitoringDiscoveryMethod = MonitoringDiscoveryMethod;
    this.scanCreateRequest = scanCreateRequest;
    this.$digitalPropertyMonitoring = $digitalPropertyMonitoring;
    this.availableDiscoveryMethods = Object.values(MonitoringDiscoveryMethod);
    this.subscriptions = new Subscription();
    this.cachedAuthorizedDomainsRegExp = [];
  }

  public get domainsTemplate(): string {
    return `<ul class="mb-0">${(this.domains ?? []).map((domain: string) => `<li>${domain}</li>`).join('')}</ul>`;
  }

  public get headers(): IScanHeader[] {
    return this.monitoring?.[$digitalPropertyMonitoring.headers];
  }

  private updateDiscoveryMethodOptions(): void {
    this.discoveryMethodOptions = this.availableDiscoveryMethods.map((val: string): { key: string; value: string } => ({
      key: this.translateService.instant(`label_page_option_${val.toLowerCase()}`),
      value: val,
    }));
  }

  public onMonitoringDiscoveryMethodChange(discoveryMethod: MonitoringDiscoveryMethod): void {
    this.clearValidators();
    switch (discoveryMethod) {
      case MonitoringDiscoveryMethod.crawl:
        this.setCrawlValidators();
        break;
      case MonitoringDiscoveryMethod.sitemap:
        this.setSiteMapValidators();
        break;
      case MonitoringDiscoveryMethod.listOfPages:
        this.setListOfPagesValidators();
        break;
      default:
        throw new Error('Undefined discovery method');
    }
  }

  public clearValidators(): void {
    this.form.get(scanCreateRequest.url).clearValidators();
    this.form.get(scanCreateRequest.limitCrawlingPagesTo).clearValidators();
    this.form.get(scanCreateRequest.crawlDeepLevel).clearValidators();
    this.form.get($digitalPropertyMonitoring.sitemapUrl).clearValidators();
    this.form.get($digitalPropertyMonitoring.pagesToMonitor).clearValidators();
  }

  public setListOfPagesValidators(): void {
    this.form
      .get($digitalPropertyMonitoring.pagesToMonitor)
      .addValidators([
        CustomValidators.validateIsEmpty,
        CustomValidators.domainValidator,
        CustomValidators.limitMaxNumberOfLines(this.maxNumberOfPages, SCAN_URLS_SIZE_LIMIT_ERROR_KEY),
        CustomValidators.domainRestrictionsValidator(this.authorizedDomainsRegExp),
      ]);
    this.form.get($digitalPropertyMonitoring.pagesToMonitor).updateValueAndValidity({ emitEvent: false });
  }

  public setSiteMapValidators(): void {
    this.form
      .get($digitalPropertyMonitoring.sitemapUrl)
      .addValidators([
        CustomValidators.validateIsEmpty,
        CustomValidators.required,
        CustomValidators.domainValidator,
        CustomValidators.sitemapValidator(() => this.authorizedDomainsRegExp),
      ]);
    this.form.get($digitalPropertyMonitoring.sitemapUrl).updateValueAndValidity({ emitEvent: false });
  }

  public setCrawlValidators(): void {
    this.form
      .get(scanCreateRequest.url)
      .setValidators([
        CustomValidators.validateIsEmpty,
        CustomValidators.domainRestrictionsValidator(this.authorizedDomainsRegExp),
      ]);
    this.form.get(scanCreateRequest.url).updateValueAndValidity({ emitEvent: false });

    this.form
      .get(scanCreateRequest.limitCrawlingPagesTo)
      .setValidators([
        CustomValidators.validateIsEmpty,
        CustomValidators.min(DEFAULT_MIN_VALUE),
        CustomValidators.max(this.maxNumberOfPages),
        CustomValidators.validateInteger,
      ]);
    this.form.get(scanCreateRequest.limitCrawlingPagesTo).updateValueAndValidity({ emitEvent: false });

    this.form
      .get(scanCreateRequest.crawlDeepLevel)
      .setValidators([
        CustomValidators.validateIsEmpty,
        CustomValidators.min(DEFAULT_MIN_VALUE),
        CustomValidators.validateInteger,
      ]);
    this.form.get(scanCreateRequest.crawlDeepLevel).updateValueAndValidity({ emitEvent: false });
  }

  private updateAvailableDiscoveryMethods(): void {
    let methods: MonitoringDiscoveryMethod[] = Object.values(MonitoringDiscoveryMethod);
    if (!this.isDpUrlPresent || this.authorizedDomainsRegExp.length === 0) {
      methods = [MonitoringDiscoveryMethod.listOfPages];
    }

    this.availableDiscoveryMethods = methods;
    this.updateDiscoveryMethodOptions();
  }

  private getMonitoringDiscoveryMethod(): MonitoringDiscoveryMethod {
    const discoveryMethod: MonitoringDiscoveryMethod =
      this.monitoring?.[$digitalPropertyMonitoring.crawler]?.discoveryMethod ?? MonitoringDiscoveryMethod.listOfPages;

    return this.availableDiscoveryMethods.includes(discoveryMethod) ? discoveryMethod : this.availableDiscoveryMethods[0];
  }

  private initializeForm(): void {
    this.form
      .get(scanCreateRequest.url)
      .setValue(
        this.monitoring?.[$digitalPropertyMonitoring.crawler]?.[$scan.url] || this.digitalProperty?.[$digitalProperty.url],
      );

    if (this.monitoring?.[$digitalPropertyMonitoring.pagesToMonitor]?.length > 0) {
      this.form
        .get($digitalPropertyMonitoring.pagesToMonitor)
        .setValue(this.monitoring[$digitalPropertyMonitoring.pagesToMonitor].join('\n'));
    } else if (this.digitalProperty?.[$digitalProperty.url]) {
      this.form.get($digitalPropertyMonitoring.pagesToMonitor).setValue(this.digitalProperty[$digitalProperty.url]);
    }

    const uniqueDomains: Set<string> = new Set(this.domains.filter((domain: string) => domain.indexOf('*') < 0));
    this.sitemapDomainExample = [...uniqueDomains][0] ?? 'https://example.com';
    if (!this.sitemapDomainExample.startsWith('http')) {
      this.sitemapDomainExample = `https://${this.sitemapDomainExample}`;
    }

    const crawler: IDPMonitoringCrawlerDefinition | IDPMonitoringSitemapDefinition =
      this.monitoring?.[$digitalPropertyMonitoring.crawler];

    if (crawler?.[$digitalPropertyMonitoring.discoveryMethod] === MonitoringDiscoveryMethod.sitemap) {
      this.form.get($digitalPropertyMonitoring.sitemapUrl).setValue(crawler[$digitalPropertyMonitoring.sitemapUrl]);
    } else if (crawler?.[$digitalPropertyMonitoring.discoveryMethod] === MonitoringDiscoveryMethod.crawl) {
      this.form.patchValue({
        [scanCreateRequest.limitCrawlingPagesTo]: crawler[$scan.limitCrawlingPagesTo],
        [scanCreateRequest.crawlDeepLevel]: crawler[$scan.crawlDeepLevel],
        [scanCreateRequest.skipHashes]: crawler[$scan.skipHashes],
        [scanCreateRequest.skipQueryParams]: crawler[$scan.skipQueryParams],
      });
    }

    this.form.get($digitalPropertyMonitoring.discoveryMethod).setValue(this.getMonitoringDiscoveryMethod(), { emitEvent: false });
    this.form.get(scanCreateRequest.preScanFlowId)?.setValue(this.monitoring?.[$digitalPropertyMonitoring.preScanFlowId] ?? null);

    const handlePopupsSelector: string = this.monitoring?.[$digitalPropertyMonitoring.handlePopupsSelector];
    this.form.get($digitalPropertyMonitoring.handlePopupsSelector)?.setValue(handlePopupsSelector);
    const waitTimeSec: number = this.monitoring?.[$digitalPropertyMonitoring.waitTimeSec];
    this.form.get($digitalPropertyMonitoring.waitTimeSec)?.setValue(waitTimeSec);
    const includeIframes: boolean = this.monitoring?.[$digitalPropertyMonitoring.includeIframes];
    this.form.get($digitalPropertyMonitoring.includeIframes)?.setValue(includeIframes);
    this.advancedService.patchScanOptions({
      handlePopupsSelector: handlePopupsSelector ?? null,
      waitTimeSec: waitTimeSec ?? null,
      includeIframes: includeIframes,
    });
  }

  private subscribeUserFlowChanges(): void {
    const preScanFlowChanges: Observable<string> = this.form.valueChanges.pipe(
      map((formValues: Partial<Record<keyof scanCreateRequest, string>>) => formValues[scanCreateRequest.preScanFlowId]),
      distinctUntilChanged(),
    );

    this.subscriptions.add(
      preScanFlowChanges.subscribe((preScanFlowId: string | null): void => {
        if (SharedCommonUtility.notNullish(preScanFlowId)) {
          this.scanAuthenticationDetails.disabled = true;
          this.form
            .get(scanCreateRequest.scanAuthenticationType)
            ?.setValue(scanAuthenticationType.no_authentication, { emitEvent: false });
          this.scanAuthenticationDetails.updateAuthValues();
        } else {
          this.scanAuthenticationDetails.disabled = false;
        }
        this.changeDetectorRef.markForCheck();
      }),
    );
  }

  public ngAfterViewInit(): void {
    this.initializeForm();
    this.onMonitoringDiscoveryMethodChange(this.getMonitoringDiscoveryMethod());
    this.subscribeUserFlowChanges();
    // Trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
    // after modifying form state in ngAfterViewInit
    this.changeDetectorRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.canUsePreScanFlows$ = this.advancedService.canUsePreScanFlows$();
    this.isInitialized = true;
    this.updateAvailableDiscoveryMethods();
  }
}
